import { Button } from '@heidi/design-system/button';
import { cn } from '@heidi/utils';
import { Loader2 } from 'lucide-react';
import dynamic from 'next/dynamic';
import type { DocumentPreviewMetadata } from './ChatDocumentPreview';

const ChatDocumentPreview = dynamic(
  () =>
    import('./ChatDocumentPreview').then((mod) => ({
      default: mod.ChatDocumentPreview,
    })),
  { ssr: false }
);

/**
 * The body's four strings, supplied by the caller.
 *
 * This component renders on surfaces that use DIFFERENT i18n systems — the
 * Evidence canvas and the preview modals read i18next `chat.*` keys that are
 * already translated into 18 locales, while the Work artifact panels use
 * react-intl. A shared leaf that picked one of them would silently drop the
 * other's translations, which is exactly what happened when these strings were
 * inlined here as new react-intl ids: every non-English user got English
 * loading and error states on surfaces that had been translated for years.
 *
 * So the leaf holds no i18n call at all and each caller passes its own.
 */
export interface DocumentPreviewBodyLabels {
  /** Shown while the preview URL is being fetched. */
  loading: string;
  /** Shown when the preview URL cannot be fetched. */
  failed: string;
  /** Fallback detail when the failure carries no `Error`. */
  unknownError: string;
  /** Label of the button that dismisses a failed preview. */
  close: string;
}

export interface DocumentPreviewBodyProps {
  labels: DocumentPreviewBodyLabels;
  url: string | null;
  /**
   * Bytes for the file. PDF and DOCX read these in process, including when
   * `url` is still null. Fetching a blob URL needs connect-src blob:.
   */
  fileBlob?: Blob;
  filename: string | null;
  password?: string | null;
  isLoading?: boolean;
  error?: Error | null;
  onClose: () => void;
  onPasswordConsumed?: () => void;
  onMetadata?: (meta: DocumentPreviewMetadata) => void;
  className?: string;
}

export function DocumentPreviewBody({
  labels,
  url,
  fileBlob,
  filename,
  password = null,
  isLoading = false,
  error = null,
  onClose,
  onPasswordConsumed,
  onMetadata,
  className,
}: DocumentPreviewBodyProps) {
  const canPreview = Boolean((url || fileBlob) && filename && !isLoading && !error);

  return (
    <div
      // eslint-disable-next-line tailwindcss/migration-from-tailwind-2 -- `overflow-clip` is a real v3 overflow utility; the rule reads it as v2's since-renamed text-overflow class
      className={cn('relative flex min-h-0 flex-1 flex-col overflow-clip', className)}
    >
      {isLoading && (
        <div className="flex min-h-0 w-full flex-1 flex-col items-center justify-center gap-3">
          <Loader2 className="size-8 animate-spin text-text-tertiary" />
          <span className="text-sm text-text-secondary">{labels.loading}</span>
        </div>
      )}

      {error && (
        <div className="flex min-h-0 w-full flex-1 flex-col items-center justify-center gap-3 px-4 text-center">
          <span className="text-sm text-validation-destructive">{labels.failed}</span>
          <span className="text-xs text-text-tertiary">
            {error instanceof Error ? error.message : labels.unknownError}
          </span>
          <Button variant="secondary" size="sm" onClick={onClose}>
            {labels.close}
          </Button>
        </div>
      )}

      {canPreview && (
        <div className="flex min-h-0 w-full flex-1 flex-col">
          <ChatDocumentPreview
            fileUrl={url ?? ''}
            fileBlob={fileBlob}
            filename={filename ?? ''}
            password={password ?? undefined}
            onMetadata={onMetadata}
            onPasswordConsumed={onPasswordConsumed}
          />
        </div>
      )}
    </div>
  );
}
